import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { WintSuperkollAPIModel, WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
import { IconDefinitionConstants, ReportConstants } from "utils/constants";
import { TableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import {
  ChartContextProvider,
  ChartToggle,
  DashboardButtonGroup,
  FilterButton,
  PageLayouts,
  ReloadEvent,
  StyledButton,
  ViewPanel,
  WidgetArea,
} from "components/common";
import InvoiceListView from "./listView";
import * as InvoiceWidgets from "./widgets";
import useCreateInvoice from "./actions/useCreateInvoice";
import useInvoiceFilterDefinition from "./forms/useInvoiceFilterDefinition";
import { Grid } from "@mui/material";
import { useInvoiceService } from "serviceHooks";
import useInvoicePermissions from "./useInvoicePemissions";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const CustomerInvoicesReport: React.FC = (props) => {
  const invoiceService = useInvoiceService();

  const { filterFormFields, filterValueFormatter, filterQueryStateHandler, apiToReportFilterFormatter } =
    useInvoiceFilterDefinition();

  const { canCreateInvoices, canUseInvoices, hasChartsPermissions } = useInvoicePermissions();
  const isAgentSalesActive = useSelector((store: AppState) => store.auth.selectedCompany.isAgentSalesActive);

  const onCreateInvoice = useCreateInvoice();

  const companyName = useSelector((store: AppState) => store.auth.selectedCompany.name ?? "");
  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CustomerInvoices);
  const fileName = `${title} - ${companyName}`;

  const tableRef = React.useRef<TableHandles>(null);

  const exportReportExcel = async () => {
    tableRef.current?.exportToExcel();
  };

  const exportReportCSV = async () => {
    tableRef.current?.exportToCSV();
  };

  return (
    <ReloadEvent
      uniqueKey={ReportConstants.ReportKeys.Invoice}
      render={(reloadKey) => (
        <PageLayouts.DashboardLayout<InvoiceFormTypes.Filter>
          entityKey={0}
          hasPermissions={canUseInvoices}
          title={title}
          topRightButtons={
            <DashboardButtonGroup
              buttons={[
                <StyledButton
                  size="sm"
                  onClick={onCreateInvoice}
                  disabled={!canCreateInvoices}
                  testId="createInvoiceButton"
                >
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CreateInvoice)}
                </StyledButton>,
                ACTION_SEPARATOR,
                <FilterButton />,
              ]}
              moreActions={[
                {
                  description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToExcel),
                  icon: IconDefinitionConstants.WSave,
                  onClickHandler: exportReportExcel,
                },
                {
                  description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToCSV),
                  icon: IconDefinitionConstants.WSave,
                  onClickHandler: exportReportCSV,
                },
              ]}
            />
          }
          filterFields={filterFormFields}
          filterValueFormatter={filterValueFormatter}
          filterQueryStateHandler={filterQueryStateHandler}
          filterCategory={WintSuperkollAPIModelCustomFilters.FilterCategory.Invoice}
          reportToAPIFilterFormatter={invoiceService.reportToAPIFilter}
          apiToReportFilterFormatter={apiToReportFilterFormatter}
          initialFilter={
            isAgentSalesActive ? { agentInvoicesId: WintSuperkollAPIModel.DownPaymentVisibility.DoNotShow } : {}
          }
        >
          <WidgetArea>
            {hasChartsPermissions() ? (
              <ChartContextProvider>
                <ViewPanel
                  title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.DistributionStatus)}
                  appearance="sm"
                  gridSize="md"
                  isCanvasContent
                >
                  <InvoiceWidgets.InvoiceStatusDistributionDoughnutChart />
                </ViewPanel>

                <ViewPanel
                  title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.InvoicedAmount)}
                  appearance="sm"
                  gridSize="md"
                  isCanvasContent
                  topRightContent={<ChartToggle />}
                >
                  <InvoiceWidgets.InvoicedAmountBarChart />
                </ViewPanel>
              </ChartContextProvider>
            ) : (
              <></>
            )}

            <Grid container item>
              <InvoiceListView includeSummary={true} reloadKey={reloadKey} ref={tableRef} exportFileName={fileName} />
            </Grid>

            {/* <ViewPanel
        title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.WhenDoTheInvoiceDue)}
        appearance="sm"
        gridSize="lg"
        isCanvasContent
      >
        <InvoiceWidgets.InvoiceDueBarChart />
      </ViewPanel> */}
          </WidgetArea>
        </PageLayouts.DashboardLayout>
      )}
    />
  );
};

export default CustomerInvoicesReport;
